import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Search,
  Plus,
  Eye,
  Heart,
  Bookmark,
  MessageCircle,
  Loader2,
  Filter,
  Star,
  Lightbulb,
  Users,
  Clock,
  ChevronRight,
  Award,
  TrendingUp,
  Send,
  MoreHorizontal,
  CheckCircle2,
  Archive,
  X,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const categoryNames = {
  akicilik: 'Akıcılık',
  cozumleme: 'Çözümleme',
  kelime: 'Kelime',
  anlama: 'Anlama',
  ses_bilgisi: 'Ses Bilgisi',
  yazma: 'Yazma',
  mudahale: 'Müdahale',
  sinif_yonetimi: 'Sınıf Yönetimi',
  veli_katilimi: 'Veli Katılımı',
  degerlendirme: 'Değerlendirme',
};

const categoryColors = {
  akicilik: 'bg-blue-100 text-blue-700',
  cozumleme: 'bg-purple-100 text-purple-700',
  kelime: 'bg-green-100 text-green-700',
  anlama: 'bg-amber-100 text-amber-700',
  ses_bilgisi: 'bg-pink-100 text-pink-700',
  yazma: 'bg-indigo-100 text-indigo-700',
  mudahale: 'bg-red-100 text-red-700',
  sinif_yonetimi: 'bg-teal-100 text-teal-700',
  veli_katilimi: 'bg-orange-100 text-orange-700',
  degerlendirme: 'bg-cyan-100 text-cyan-700',
};

const statusNames = {
  taslak: 'Taslak',
  beklemede: 'Onay Bekliyor',
  yayinda: 'Yayında',
  one_cikan: 'Öne Çıkan',
  arsiv: 'Arşiv',
};

const statusColors = {
  taslak: 'bg-slate-100 text-slate-700',
  beklemede: 'bg-yellow-100 text-yellow-700',
  yayinda: 'bg-green-100 text-green-700',
  one_cikan: 'bg-secondary/20 text-secondary',
  arsiv: 'bg-gray-100 text-gray-500',
};

const gradeLevelNames = {
  okul_oncesi: 'Okul Öncesi',
  sinif_1: '1. Sınıf',
  sinif_2: '2. Sınıf',
  sinif_3: '3. Sınıf',
  sinif_4: '4. Sınıf',
  sinif_5: '5. Sınıf',
  sinif_6: '6. Sınıf',
  sinif_7: '7. Sınıf',
  sinif_8: '8. Sınıf',
};

export const CasesPage = () => {
  const { user, hasRole } = useAuth();
  const [cases, setCases] = useState([]);
  const [featuredCases, setFeaturedCases] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [selectedCase, setSelectedCase] = useState(null);
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [filters, setFilters] = useState({
    search: '',
    category: 'all',
    grade_level: 'all',
    status: 'all',
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [newCase, setNewCase] = useState({
    title: '',
    summary: '',
    challenge: '',
    approach: '',
    implementation: '',
    results: '',
    lessons_learned: '',
    tips: '',
    category: 'akicilik',
    grade_levels: [],
    student_count: '',
    duration_weeks: '',
    tags: '',
  });

  const isAdmin = hasRole(['tenant_owner', 'center_director', 'reading_coach']);

  const fetchCases = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.category !== 'all') params.append('category', filters.category);
      if (filters.grade_level !== 'all') params.append('grade_level', filters.grade_level);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.search) params.append('search', filters.search);
      if (activeTab === 'bookmarks') params.append('bookmarks_only', 'true');
      if (activeTab === 'my_cases') params.append('my_cases', 'true');
      
      const response = await fetch(`${API_URL}/api/cases?${params}`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setCases(data);
      }
    } catch (error) {
      console.error('Failed to fetch cases:', error);
      toast.error('İyi örnekler yüklenirken hata oluştu');
    }
  };

  const fetchFeatured = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cases/featured?limit=3`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setFeaturedCases(data);
      }
    } catch (error) {
      console.error('Failed to fetch featured:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API_URL}/api/cases/stats`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const fetchComments = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/comments`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setComments(data);
      }
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchCases(), fetchFeatured(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, []);

  useEffect(() => {
    if (!loading) {
      fetchCases();
    }
  }, [filters.category, filters.grade_level, filters.status, activeTab]);

  useEffect(() => {
    if (selectedCase) {
      fetchComments(selectedCase.case_id);
    }
  }, [selectedCase]);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchCases();
  };

  const handleToggleLike = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/like`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        setCases(prev => prev.map(c => 
          c.case_id === caseId 
            ? { ...c, is_liked: result.is_liked, like_count: c.like_count + (result.is_liked ? 1 : -1) }
            : c
        ));
        if (selectedCase?.case_id === caseId) {
          setSelectedCase(prev => ({
            ...prev,
            is_liked: result.is_liked,
            like_count: prev.like_count + (result.is_liked ? 1 : -1)
          }));
        }
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleToggleBookmark = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/bookmark`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        setCases(prev => prev.map(c => 
          c.case_id === caseId 
            ? { ...c, is_bookmarked: result.is_bookmarked, bookmark_count: c.bookmark_count + (result.is_bookmarked ? 1 : -1) }
            : c
        ));
        if (selectedCase?.case_id === caseId) {
          setSelectedCase(prev => ({
            ...prev,
            is_bookmarked: result.is_bookmarked,
            bookmark_count: prev.bookmark_count + (result.is_bookmarked ? 1 : -1)
          }));
        }
        toast.success(result.message);
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleApprove = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/approve`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('İyi örnek onaylandı');
        fetchCases();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Onaylama başarısız');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleFeature = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/feature`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        const result = await response.json();
        toast.success(result.message);
        fetchCases();
        fetchFeatured();
        fetchStats();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleArchive = async (caseId) => {
    try {
      const response = await fetch(`${API_URL}/api/cases/${caseId}/archive`, {
        method: 'POST',
        credentials: 'include',
      });
      
      if (response.ok) {
        toast.success('İyi örnek arşivlendi');
        fetchCases();
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    setCommentLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/cases/${selectedCase.case_id}/comments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ content: newComment }),
      });
      
      if (response.ok) {
        const comment = await response.json();
        setComments(prev => [comment, ...prev]);
        setNewComment('');
        setSelectedCase(prev => ({
          ...prev,
          comment_count: prev.comment_count + 1
        }));
        toast.success('Yorum eklendi');
      }
    } catch (error) {
      toast.error('Yorum eklenemedi');
    } finally {
      setCommentLoading(false);
    }
  };

  const handleAddCase = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    
    try {
      const payload = {
        ...newCase,
        tips: newCase.tips.split('\n').map(t => t.trim()).filter(t => t),
        tags: newCase.tags.split(',').map(t => t.trim()).filter(t => t),
        student_count: newCase.student_count ? parseInt(newCase.student_count) : null,
        duration_weeks: newCase.duration_weeks ? parseInt(newCase.duration_weeks) : null,
      };
      
      const response = await fetch(`${API_URL}/api/cases`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      
      if (response.ok) {
        toast.success('İyi örnek oluşturuldu');
        setIsAddDialogOpen(false);
        setNewCase({
          title: '',
          summary: '',
          challenge: '',
          approach: '',
          implementation: '',
          results: '',
          lessons_learned: '',
          tips: '',
          category: 'akicilik',
          grade_levels: [],
          student_count: '',
          duration_weeks: '',
          tags: '',
        });
        fetchCases();
        fetchStats();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Oluşturulamadı');
      }
    } catch (error) {
      toast.error('Bir hata oluştu');
    } finally {
      setAddLoading(false);
    }
  };

  const toggleGradeLevel = (level) => {
    setNewCase(prev => ({
      ...prev,
      grade_levels: prev.grade_levels.includes(level)
        ? prev.grade_levels.filter(l => l !== level)
        : [...prev.grade_levels, level]
    }));
  };

  const CaseCard = ({ caseItem, featured = false }) => (
    <Card 
      className={`hover:shadow-md transition-shadow cursor-pointer overflow-hidden ${
        featured ? 'border-secondary/30 bg-gradient-to-br from-white to-secondary/5' : ''
      }`}
      onClick={() => setSelectedCase(caseItem)}
      data-testid={`case-card-${caseItem.case_id}`}
    >
      <CardContent className="pt-6">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge className={categoryColors[caseItem.category]}>
              {categoryNames[caseItem.category]}
            </Badge>
            {caseItem.status === 'one_cikan' && (
              <Badge className="bg-secondary/20 text-secondary">
                <Star className="h-3 w-3 mr-1 fill-current" />
                Öne Çıkan
              </Badge>
            )}
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleLike(caseItem.case_id); }}>
                <Heart className={`mr-2 h-4 w-4 ${caseItem.is_liked ? 'fill-red-500 text-red-500' : ''}`} />
                {caseItem.is_liked ? 'Beğeniyi Kaldır' : 'Beğen'}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleToggleBookmark(caseItem.case_id); }}>
                <Bookmark className={`mr-2 h-4 w-4 ${caseItem.is_bookmarked ? 'fill-primary text-primary' : ''}`} />
                {caseItem.is_bookmarked ? 'Kayıtlılardan Çıkar' : 'Kaydet'}
              </DropdownMenuItem>
              {isAdmin && caseItem.status === 'beklemede' && (
                <>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleApprove(caseItem.case_id); }}>
                    <CheckCircle2 className="mr-2 h-4 w-4 text-green-600" />
                    Onayla
                  </DropdownMenuItem>
                </>
              )}
              {hasRole(['tenant_owner', 'center_director']) && caseItem.status === 'yayinda' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleFeature(caseItem.case_id); }}>
                  <Star className="mr-2 h-4 w-4 text-secondary" />
                  Öne Çıkar
                </DropdownMenuItem>
              )}
              {hasRole(['tenant_owner', 'center_director']) && caseItem.status === 'one_cikan' && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleFeature(caseItem.case_id); }}>
                  <Star className="mr-2 h-4 w-4" />
                  Öne Çıkarmayı Kaldır
                </DropdownMenuItem>
              )}
              {hasRole(['tenant_owner', 'center_director']) && (
                <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleArchive(caseItem.case_id); }}>
                  <Archive className="mr-2 h-4 w-4" />
                  Arşivle
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        
        <h3 className="font-semibold text-primary-900 line-clamp-2 mb-2">
          {caseItem.title}
        </h3>
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
          {caseItem.summary}
        </p>
        
        <div className="flex flex-wrap gap-1 mb-3">
          {caseItem.grade_levels?.slice(0, 2).map((level, idx) => (
            <Badge key={idx} variant="outline" className="text-xs">
              {gradeLevelNames[level] || level}
            </Badge>
          ))}
          {caseItem.duration_weeks && (
            <Badge variant="outline" className="text-xs">
              <Clock className="h-3 w-3 mr-1" />
              {caseItem.duration_weeks} hafta
            </Badge>
          )}
          {caseItem.student_count && (
            <Badge variant="outline" className="text-xs">
              <Users className="h-3 w-3 mr-1" />
              {caseItem.student_count} öğrenci
            </Badge>
          )}
        </div>
        
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-3 border-t">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <Eye className="h-3 w-3" />
              {caseItem.view_count}
            </span>
            <span className="flex items-center gap-1">
              <Heart className={`h-3 w-3 ${caseItem.is_liked ? 'fill-red-500 text-red-500' : ''}`} />
              {caseItem.like_count}
            </span>
            <span className="flex items-center gap-1">
              <MessageCircle className="h-3 w-3" />
              {caseItem.comment_count}
            </span>
          </div>
          <span>{caseItem.submitter_name}</span>
        </div>
      </CardContent>
    </Card>
  );

  const CaseDetailModal = () => {
    if (!selectedCase) return null;
    
    return (
      <Dialog open={!!selectedCase} onOpenChange={() => setSelectedCase(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center gap-2 mb-2">
              <Badge className={categoryColors[selectedCase.category]}>
                {categoryNames[selectedCase.category]}
              </Badge>
              <Badge className={statusColors[selectedCase.status]}>
                {statusNames[selectedCase.status]}
              </Badge>
            </div>
            <DialogTitle className="text-xl">{selectedCase.title}</DialogTitle>
            <DialogDescription className="text-base">
              {selectedCase.summary}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6 py-4">
            {/* Author Info */}
            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
              <Avatar>
                <AvatarImage src={selectedCase.submitter_picture} />
                <AvatarFallback>{selectedCase.submitter_name?.[0]}</AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{selectedCase.submitter_name}</p>
                <p className="text-sm text-muted-foreground">
                  {selectedCase.grade_levels?.map(l => gradeLevelNames[l]).join(', ')}
                  {selectedCase.student_count && ` • ${selectedCase.student_count} öğrenci`}
                  {selectedCase.duration_weeks && ` • ${selectedCase.duration_weeks} hafta`}
                </p>
              </div>
            </div>

            {/* Challenge */}
            <div>
              <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-sm">1</span>
                Karşılaşılan Zorluk
              </h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.challenge}</p>
            </div>

            {/* Approach */}
            <div>
              <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm">2</span>
                Yaklaşım
              </h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.approach}</p>
            </div>

            {/* Implementation */}
            <div>
              <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center text-sm">3</span>
                Uygulama
              </h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.implementation}</p>
            </div>

            {/* Results */}
            <div>
              <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                <span className="h-6 w-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-sm">4</span>
                Sonuçlar
              </h4>
              <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.results}</p>
            </div>

            {/* Lessons Learned */}
            {selectedCase.lessons_learned && (
              <div>
                <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                  <Lightbulb className="h-5 w-5 text-secondary" />
                  Çıkarılan Dersler
                </h4>
                <p className="text-sm text-muted-foreground whitespace-pre-wrap">{selectedCase.lessons_learned}</p>
              </div>
            )}

            {/* Tips */}
            {selectedCase.tips?.length > 0 && (
              <div>
                <h4 className="font-semibold text-primary-900 mb-2 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  Öneriler
                </h4>
                <ul className="space-y-1">
                  {selectedCase.tips.map((tip, idx) => (
                    <li key={idx} className="text-sm text-muted-foreground flex items-start gap-2">
                      <ChevronRight className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Tags */}
            {selectedCase.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {selectedCase.tags.map((tag, idx) => (
                  <Badge key={idx} variant="outline" className="text-xs">
                    #{tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Engagement */}
            <div className="flex items-center gap-4 pt-4 border-t">
              <Button
                variant={selectedCase.is_liked ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleToggleLike(selectedCase.case_id)}
              >
                <Heart className={`h-4 w-4 mr-1 ${selectedCase.is_liked ? 'fill-current' : ''}`} />
                {selectedCase.like_count}
              </Button>
              <Button
                variant={selectedCase.is_bookmarked ? "secondary" : "outline"}
                size="sm"
                onClick={() => handleToggleBookmark(selectedCase.case_id)}
              >
                <Bookmark className={`h-4 w-4 mr-1 ${selectedCase.is_bookmarked ? 'fill-current' : ''}`} />
                Kaydet
              </Button>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Eye className="h-4 w-4" />
                {selectedCase.view_count} görüntülenme
              </span>
            </div>

            {/* Comments Section */}
            <div className="pt-4 border-t">
              <h4 className="font-semibold text-primary-900 mb-4 flex items-center gap-2">
                <MessageCircle className="h-5 w-5" />
                Yorumlar ({selectedCase.comment_count})
              </h4>
              
              {/* Add Comment */}
              <form onSubmit={handleAddComment} className="flex gap-2 mb-4">
                <Input
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  placeholder="Yorumunuzu yazın..."
                  className="flex-1"
                />
                <Button type="submit" disabled={commentLoading || !newComment.trim()}>
                  {commentLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </form>

              {/* Comment List */}
              <div className="space-y-4">
                {comments.map(comment => (
                  <div key={comment.comment_id} className="flex gap-3">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={comment.user_picture} />
                      <AvatarFallback className="text-xs">{comment.user_name?.[0]}</AvatarFallback>
                    </Avatar>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{comment.user_name}</span>
                        <span className="text-xs text-muted-foreground">
                          {new Date(comment.created_at).toLocaleDateString('tr-TR')}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{comment.content}</p>
                    </div>
                  </div>
                ))}
                {comments.length === 0 && (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Henüz yorum yok. İlk yorumu siz yapın!
                  </p>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cases-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-heading font-bold text-primary-900">
            İyi Örnekler
          </h1>
          <p className="text-muted-foreground">
            {stats?.total || 0} örnek • {stats?.featured || 0} öne çıkan
          </p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-case-btn">
              <Plus className="mr-2 h-4 w-4" />
              Örnek Paylaş
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Yeni İyi Örnek Paylaş</DialogTitle>
              <DialogDescription>
                Başarılı uygulama örneğinizi meslektaşlarınızla paylaşın
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleAddCase}>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Başlık *</Label>
                  <Input
                    id="title"
                    value={newCase.title}
                    onChange={(e) => setNewCase({...newCase, title: e.target.value})}
                    placeholder="Örneğin kısa ve açıklayıcı bir başlığı"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="summary">Özet *</Label>
                  <Textarea
                    id="summary"
                    value={newCase.summary}
                    onChange={(e) => setNewCase({...newCase, summary: e.target.value})}
                    placeholder="Örneğin kısa bir özeti (max 200 karakter)"
                    maxLength={200}
                    required
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Kategori</Label>
                    <Select
                      value={newCase.category}
                      onValueChange={(value) => setNewCase({...newCase, category: value})}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Object.entries(categoryNames).map(([value, label]) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Süre (hafta)</Label>
                    <Input
                      type="number"
                      value={newCase.duration_weeks}
                      onChange={(e) => setNewCase({...newCase, duration_weeks: e.target.value})}
                      placeholder="6"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Sınıf Düzeyleri</Label>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(gradeLevelNames).map(([value, label]) => (
                      <Button
                        key={value}
                        type="button"
                        variant={newCase.grade_levels.includes(value) ? "default" : "outline"}
                        size="sm"
                        onClick={() => toggleGradeLevel(value)}
                      >
                        {label}
                      </Button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="challenge">Karşılaşılan Zorluk *</Label>
                  <Textarea
                    id="challenge"
                    value={newCase.challenge}
                    onChange={(e) => setNewCase({...newCase, challenge: e.target.value})}
                    placeholder="Hangi sorun veya zorlukla karşılaştınız?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="approach">Yaklaşım *</Label>
                  <Textarea
                    id="approach"
                    value={newCase.approach}
                    onChange={(e) => setNewCase({...newCase, approach: e.target.value})}
                    placeholder="Hangi yaklaşımı/stratejiyi kullandınız?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="implementation">Uygulama *</Label>
                  <Textarea
                    id="implementation"
                    value={newCase.implementation}
                    onChange={(e) => setNewCase({...newCase, implementation: e.target.value})}
                    placeholder="Uygulamayı adım adım nasıl gerçekleştirdiniz?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="results">Sonuçlar *</Label>
                  <Textarea
                    id="results"
                    value={newCase.results}
                    onChange={(e) => setNewCase({...newCase, results: e.target.value})}
                    placeholder="Ne gibi sonuçlar elde ettiniz?"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lessons_learned">Çıkarılan Dersler</Label>
                  <Textarea
                    id="lessons_learned"
                    value={newCase.lessons_learned}
                    onChange={(e) => setNewCase({...newCase, lessons_learned: e.target.value})}
                    placeholder="Bu deneyimden ne öğrendiniz?"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="tips">Öneriler (her satıra bir öneri)</Label>
                  <Textarea
                    id="tips"
                    value={newCase.tips}
                    onChange={(e) => setNewCase({...newCase, tips: e.target.value})}
                    placeholder="Meslektaşlarınıza önerileriniz..."
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Öğrenci Sayısı</Label>
                    <Input
                      type="number"
                      value={newCase.student_count}
                      onChange={(e) => setNewCase({...newCase, student_count: e.target.value})}
                      placeholder="8"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="tags">Etiketler</Label>
                    <Input
                      id="tags"
                      value={newCase.tags}
                      onChange={(e) => setNewCase({...newCase, tags: e.target.value})}
                      placeholder="akıcılık, müdahale, 2. sınıf"
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                  İptal
                </Button>
                <Button type="submit" disabled={addLoading}>
                  {addLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Oluştur
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Featured Cases */}
      {featuredCases.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-primary-900 mb-3 flex items-center gap-2">
            <Star className="h-5 w-5 text-secondary fill-secondary" />
            Öne Çıkan Örnekler
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {featuredCases.map(caseItem => (
              <CaseCard key={caseItem.case_id} caseItem={caseItem} featured />
            ))}
          </div>
        </div>
      )}

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-primary-900">{stats.total}</div>
              <p className="text-sm text-muted-foreground">Toplam Örnek</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-secondary">{stats.featured}</div>
              <p className="text-sm text-muted-foreground">Öne Çıkan</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-green-600">{stats.by_status?.yayinda || 0}</div>
              <p className="text-sm text-muted-foreground">Yayında</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4">
              <div className="text-2xl font-bold text-yellow-600">{stats.pending_approval}</div>
              <p className="text-sm text-muted-foreground">Onay Bekliyor</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">Tüm Örnekler</TabsTrigger>
          <TabsTrigger value="bookmarks">Kaydettiklerim</TabsTrigger>
          <TabsTrigger value="my_cases">Paylaştıklarım</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSearch} className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Örnek ara..."
                    value={filters.search}
                    onChange={(e) => setFilters({...filters, search: e.target.value})}
                    className="pl-10"
                  />
                </div>
                <Select value={filters.category} onValueChange={(value) => setFilters({...filters, category: value})}>
                  <SelectTrigger className="w-full md:w-[180px]">
                    <SelectValue placeholder="Kategori" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Kategoriler</SelectItem>
                    {Object.entries(categoryNames).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filters.grade_level} onValueChange={(value) => setFilters({...filters, grade_level: value})}>
                  <SelectTrigger className="w-full md:w-[140px]">
                    <SelectValue placeholder="Sınıf" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tüm Sınıflar</SelectItem>
                    {Object.entries(gradeLevelNames).map(([value, label]) => (
                      <SelectItem key={value} value={value}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {isAdmin && (
                  <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                    <SelectTrigger className="w-full md:w-[140px]">
                      <SelectValue placeholder="Durum" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Tüm Durumlar</SelectItem>
                      {Object.entries(statusNames).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Button type="submit" variant="secondary">
                  <Filter className="mr-2 h-4 w-4" />
                  Filtrele
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Cases Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {cases.map(caseItem => (
              <CaseCard key={caseItem.case_id} caseItem={caseItem} />
            ))}
          </div>
          
          {cases.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                {activeTab === 'bookmarks' 
                  ? 'Henüz kaydettiğiniz örnek yok'
                  : activeTab === 'my_cases'
                    ? 'Henüz paylaştığınız örnek yok'
                    : 'Örnek bulunamadı'
                }
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>

      {/* Case Detail Modal */}
      <CaseDetailModal />
    </div>
  );
};

export default CasesPage;
